/**
 * conference-week-hero block
 *
 * Conference landing-page hero: cream band, dotted eyebrow, display heading,
 * emphasised subhead, body paragraphs, and up to two CTAs, beside a 16:9
 * illustration.
 *
 * Differs from `rsa-intro-split` in ways that could not be reached with its
 * props: the cream band, the pink accent dot, a second (outline) CTA, and a
 * fluid display heading rather than a fixed 28.4px / 31px step.
 *
 * The image is 16:9 by contract (`aspect-video` + `object-cover`). Supply
 * 16:9 artwork; anything else is centre-cropped.
 */
import { Image } from "../Image";

export function ConferenceWeekHero({
  eyebrow,
  heading,
  subhead,
  copy,
  button_primary,
  button_secondary,
  image,
  image_alt,
  background = "cream",
}: {
  /** Small uppercase label above the heading, preceded by a pink accent dot. */
  eyebrow?: string;
  heading?: string;
  /** Emphasised line directly under the heading. */
  subhead?: string;
  /** One paragraph per entry. */
  copy?: string[];
  button_primary?: { text: string; url: string };
  /** Outline CTA. Use a `#anchor` url to jump down the page. */
  button_secondary?: { text: string; url: string };
  /** 16:9 artwork. */
  image?: string;
  image_alt?: string;
  /** Band behind the hero. "cream" (default) or "none" for the page default. */
  background?: "cream" | "none";
}) {
  const isExternal = (url?: string) => Boolean(url?.startsWith("http"));

  return (
    <div className={background === "cream" ? "bg-cream" : ""}>
      <div className="px-4 md:px-8 py-11 lg:py-16">
        <div className="max-w-[1300px] mx-auto grid gap-8 lg:gap-16 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] items-center">
          <div>
            {eyebrow && (
              <p className="flex items-center gap-2.5 font-body text-gray-60 text-[11px] font-semibold uppercase tracking-[0.08em] mb-5">
                <span
                  aria-hidden="true"
                  className="w-[7px] h-[7px] rounded-full bg-pink-accent shrink-0"
                />
                {eyebrow}
              </p>
            )}

            {heading && (
              <h1 className="font-display text-charcoal font-bold text-[34px] lg:text-[52px] leading-[1.05] tracking-[-0.025em] text-balance mb-5">
                {heading}
              </h1>
            )}

            {subhead && (
              <p className="font-body text-charcoal text-[17px] font-semibold leading-snug max-w-[44ch] mb-5">
                {subhead}
              </p>
            )}

            {copy && copy.length > 0 && (
              <div className="flex flex-col gap-3.5 max-w-[52ch] mb-8">
                {copy.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 40)}
                    className="font-body text-gray-80 text-[15.5px] leading-relaxed"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            )}

            {(button_primary || button_secondary) && (
              <div className="flex items-center gap-3 flex-wrap">
                {button_primary?.url && (
                  <a
                    href={button_primary.url}
                    target={isExternal(button_primary.url) ? "_blank" : undefined}
                    rel={isExternal(button_primary.url) ? "noopener" : undefined}
                    className="inline-flex items-center bg-teal-cta hover:bg-teal-cta-hover text-white font-body text-sm font-semibold rounded-md px-4.5 py-3 leading-none transition-colors duration-200"
                  >
                    {button_primary.text}
                  </a>
                )}
                {button_secondary?.url && (
                  <a
                    href={button_secondary.url}
                    target={isExternal(button_secondary.url) ? "_blank" : undefined}
                    rel={isExternal(button_secondary.url) ? "noopener" : undefined}
                    className="inline-flex items-center text-teal-dark hover:border-teal-dark font-body text-sm font-semibold rounded-md border border-gray-60/60 px-3.5 py-3 leading-none transition-colors duration-200"
                  >
                    {button_secondary.text}
                  </a>
                )}
              </div>
            )}
          </div>

          {image && (
            <div className="w-full aspect-video rounded-xl overflow-hidden bg-teal-dark">
              <Image
                src={image}
                alt={image_alt || ""}
                sizes="(min-width: 1024px) 660px, 100vw"
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
